import * as R from 'ramda';
import { createSelector } from 'reselect';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// report-common
import { getReportSelectors } from '../../report-common';
//////////////////////////////////////////////////

const {
  store,
  makeSelectItemList,
  makeSelectUsedReport,
  makeSelectPagination,
  makeSelectTotalCount,
  makeSelectListLoading,
  makeSelectPageVisited,
  makeSelectReportStatus,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectTableTitleFilters,
} = getReportSelectors('branch');

const makeSelectBranchesTree = () => createSelector(
  store,
  (branch) => G.getPropFromObject('treeBranches', branch),
);

const searchTree = (tree, currentBranch) => {
  const children = G.getPropFromObject('children', tree);

  if (R.eqProps(GC.FIELD_GUID, tree, currentBranch)) {
    return tree;
  } else if (G.isNotNilAndNotEmpty(children)) {
    let i;
    let result = null;

    for (i = 0; result == null && i < R.length(children); i++) {
      result = searchTree(children[i], currentBranch);
    }

    if (G.isNotNilAndNotEmpty(result)) return result;
  }
};

const makeSelectBranchesTreeFromCurrentAndDown = () => createSelector(
  store,
  (state) => {
    const { treeBranches, currentBranch } = state;

    return searchTree(treeBranches, currentBranch);
  },
);

const getListFromTree = (tree) => {
  const array = [];

  const mapTree = (childTree) => {
    array.push(R.omit('children', childTree));

    const children = G.getPropFromObject('children', childTree);

    if (G.isNotNilAndNotEmpty(children)) {
      let i;

      for (i = 0; i < R.length(children); i++) {
        mapTree(children[i]);
      }
    }
  };

  mapTree(tree);

  return R.indexBy(R.prop(GC.FIELD_GUID), array);
};

const makeSelectBranchListFromCurrentAndDown = () => createSelector(
  store,
  (state) => {
    const { treeBranches, currentBranch } = state;

    const tree = searchTree(treeBranches, currentBranch);

    return getListFromTree(tree);
  },
);

const makeSelectBranchById = () => createSelector(
  store,
  ({ branch }) => branch,
);

const makeSelectBranchCompanyDetails = () => createSelector(
  store,
  ({ branch }) => G.getPropFromObject('CompanyDetails', branch),
);

const makeSelectBranchSetting = () => createSelector(
  store,
  ({ branch }) => G.getPropFromObject('BranchSetting', branch),
);

const makeSelectCurrentBranch = () => createSelector(
  store,
  ({ currentBranch }) => currentBranch,
);

const makeSelectCurrentBranchGuid = () => createSelector(
  store,
  ({ currentBranch }) => G.getGuidFromObject(currentBranch),
);

const makeSelectCollapsedGroup = () => createSelector(
  store,
  ({ collapsedGroup }) => collapsedGroup,
);

const makeSelectBranchRefs = () => createSelector(
  store,
  (branch) => G.getPropFromObject(GC.UI_FIELD_REFERENCES, branch),
);

const makeSelectEditBranchBillToLocation = () => createSelector(
  store,
  ({ editBranchBillToLocation }) => editBranchBillToLocation,
);

const makeSelectEditBranchPrimaryLocation = () => createSelector(
  store,
  ({ editBranchPrimaryLocation }) => editBranchPrimaryLocation,
);

const makeSelectCurrentBranchConfigs = () => createSelector(
  store,
  ({ currentBranchConfigs }) => {
    const documentTypeOptions = G.getFullOptionsFromDropdownConfigStore(
      currentBranchConfigs,
      GC.COMMUNICATION_BRANCH_DOCUMENT_TYPE,
    );

    const locationTypeOptions = G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      currentBranchConfigs,
      GC.TEMPLATES_LOCATION_TYPE,
      true,
    );

    const subTypeOptions = G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      currentBranchConfigs,
      GC.GENERAL_BRANCH_SUBTYPE,
      true,
    );

    return { subTypeOptions, documentTypeOptions, locationTypeOptions };
  },
);

const makeSelectEditBranchDocumentList = () => createSelector(
  store,
  ({ editBranchDocumentList }) => editBranchDocumentList,
);

const makeSelectImportMappers = () => createSelector(
  store,
  ({ importMappers }) => importMappers,
);

const makeSelectAdditionalLocationList = () => createSelector(
  store,
  ({ additionalLocationList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(additionalLocationList),
);

const makeSelectBranchGuidByBranchId = () => createSelector(
  store,
  ({ branch }) => R.path(['CompanyDetails', GC.FIELD_GUID], branch),
);

const makeSelectSelectedList = () => createSelector(
  store,
  (state) => R.compose(
    R.keys,
    R.filter(({ selected }) => selected),
    R.pathOr([], ['itemList']),
  )(state),
);

const makeSelectCurrentBranchType = () => createSelector(
  store,
  R.path(['currentBranch', GC.BRANCH_TYPE]),
);

const makeSelectCommissionAssigneeByType = () => createSelector(
  store,
  ({ commissionAssigneeByType }) => commissionAssigneeByType,
);

const makeSelectMasterSettings = () => createSelector(
  store,
  ({ masterSettings }) => masterSettings,
);

export {
  store,
  makeSelectItemList,
  makeSelectPagination,
  makeSelectUsedReport,
  makeSelectBranchById,
  makeSelectTotalCount,
  makeSelectBranchRefs,
  makeSelectPageVisited,
  makeSelectListLoading,
  makeSelectSelectedList,
  makeSelectReportStatus,
  makeSelectBranchesTree,
  makeSelectCurrentBranch,
  makeSelectImportMappers,
  makeSelectBranchSetting,
  makeSelectMasterSettings,
  makeSelectCollapsedGroup,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectCurrentBranchType,
  makeSelectCurrentBranchGuid,
  makeSelectTableTitleFilters,
  makeSelectBranchCompanyDetails,
  makeSelectBranchGuidByBranchId,
  makeSelectCurrentBranchConfigs,
  makeSelectEditBranchDocumentList,
  makeSelectAdditionalLocationList,
  makeSelectCommissionAssigneeByType,
  makeSelectEditBranchBillToLocation,
  makeSelectEditBranchPrimaryLocation,
  makeSelectBranchListFromCurrentAndDown,
  makeSelectBranchesTreeFromCurrentAndDown,
};
